import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── AUTH ────────────────────────────────────────────────────
export const signUp = async (email, password, username) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { username } },
  });
  if (error) throw error;
  // create profile row
  if (data.user) {
    await supabase.from('profiles').insert([{ id: data.user.id, username, email, telegram_number: null }]);
  }
  return data;
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  });
  if (error) throw error;
  return data;
};

// Creates a profile row for OAuth users who don't have one yet
export const ensureProfile = async (user) => {
  const { data: existing } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', user.id)
    .maybeSingle();
  if (existing) return;

  // Build a username from their Google display name or email prefix
  let base = (user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'user')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 20) || 'user';

  // Find a unique username
  let username = base;
  let n = 1;
  while (true) {
    const { data: taken } = await supabase.from('profiles').select('id').eq('username', username).maybeSingle();
    if (!taken) break;
    username = base + n++;
  }

  await supabase.from('profiles').insert([{ id: user.id, username, email: user.email, telegram_number: null }]);
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

// ─── PROFILE ─────────────────────────────────────────────────
export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
};

export const updateProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// ─── LISTINGS ────────────────────────────────────────────────
export const createListing = async (listing) => {
  const { data, error } = await supabase
    .from('listings')
    .insert([listing])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const getListings = async () => {
  const { data, error } = await supabase
    .from('listings')
    .select('*, profiles(username, payment_qr_url), queue_entries(count)')
    .eq('hidden', false)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
};

export const getListing = async (id) => {
  const { data, error } = await supabase
    .from('listings')
    .select('*, profiles(id, username, payment_qr_url, telegram_number), queue_entries(id, user_id, position, created_at, profiles(username, telegram_number, email))')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
};

export const updateListingStatus = async (id, status) => {
  const { data, error } = await supabase
    .from('listings')
    .update({ status })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};


export const updateListing = async (id, updates) => {
  const { data, error } = await supabase
    .from('listings')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const getUserListings = async (userId) => {
  const { data, error } = await supabase
    .from('listings')
    .select('*, queue_entries(count)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
};

export const toggleListingHidden = async (id, hidden) => {
  const { data, error } = await supabase
    .from('listings')
    .update({ hidden })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteListing = async (id) => {
  const { data, error } = await supabase.from('listings').delete().eq('id', id).select();
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Permission denied — the database blocked this delete. Run the admin RLS policy SQL in Supabase to fix this.');
};

// ─── IMAGE UPLOAD ─────────────────────────────────────────────
const compressImage = (file, maxWidth, quality) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })),
        'image/jpeg',
        quality
      );
    };
    img.src = url;
  });

export const uploadImage = async (file, userId) => {
  const compressed = await compressImage(file, 1200, 0.8);
  const fileName = `${userId}/${Date.now()}.jpg`;
  const { error } = await supabase.storage.from('listing-images').upload(fileName, compressed);
  if (error) throw error;
  const { data: { publicUrl } } = supabase.storage.from('listing-images').getPublicUrl(fileName);
  return publicUrl;
};

export const uploadQrImage = async (file, userId) => {
  const compressed = await compressImage(file, 600, 0.9);
  const fileName = `qr-${userId}.jpg`;
  const { error } = await supabase.storage.from('qr-images').upload(fileName, compressed, { upsert: true });
  if (error) throw error;
  const { data: { publicUrl } } = supabase.storage.from('qr-images').getPublicUrl(fileName);
  return publicUrl;
};


export const getProfileByUsername = async (username) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, created_at')
    .eq('username', username)
    .single();
  if (error) throw error;
  return data;
};

export const getListingsBySeller = async (userId) => {
  const { data, error } = await supabase
    .from('listings')
    .select('*, profiles(username), queue_entries(count)')
    .eq('user_id', userId)
    .eq('hidden', false)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
};

export const searchUsers = async (query) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, created_at')
    .ilike('username', `%${query}%`)
    .order('username')
    .limit(10);
  if (error) throw error;
  return data;
};

export const searchListings = async (query) => {
  const { data, error } = await supabase
    .from('listings')
    .select('*, profiles(username), queue_entries(count)')
    .ilike('title', `%${query}%`)
    .eq('hidden', false)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
};

// ─── QUEUE ────────────────────────────────────────────────────
export const joinQueue = async (listingId, userId) => {
  // Check not already in queue
  const { data: existing } = await supabase
    .from('queue_entries')
    .select('id')
    .eq('listing_id', listingId)
    .eq('user_id', userId)
    .single();
  if (existing) throw new Error('You are already in the queue for this item.');

  const { data: top } = await supabase
    .from('queue_entries')
    .select('position')
    .eq('listing_id', listingId)
    .order('position', { ascending: false })
    .limit(1);

  const nextPosition = top && top.length > 0 ? top[0].position + 1 : 1;

  const { data, error } = await supabase
    .from('queue_entries')
    .insert([{ listing_id: listingId, user_id: userId, position: nextPosition }])
    .select()
    .single();
  if (error) throw error;

  // Notify the seller
  try {
    const { data: listing } = await supabase
      .from('listings')
      .select('user_id, title, profiles(username)')
      .eq('id', listingId)
      .single();

    const { data: buyer } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', userId)
      .single();

    if (listing && listing.user_id !== userId) {
      const buyerName = buyer?.username || 'Someone';
      const itemTitle = listing.title || 'your item';
      await supabase.from('notifications').insert([{
        user_id: listing.user_id,
        from_user: userId,
        listing_id: listingId,
        message: buyerName + ' made an offer to buy "' + itemTitle + '"'
      }]);
    }
  } catch (e) {
    console.error('Could not create notification:', e.message);
  }

  return data;
};

export const leaveQueue = async (listingId, userId) => {
  const { error } = await supabase
    .from('queue_entries')
    .delete()
    .eq('listing_id', listingId)
    .eq('user_id', userId);
  if (error) throw error;
};

export const removeFromQueue = async (entryId) => {
  const { error } = await supabase
    .from('queue_entries')
    .delete()
    .eq('id', entryId);
  if (error) throw error;
};

export const getUserQueueEntry = async (listingId, userId) => {
  const { data } = await supabase
    .from('queue_entries')
    .select('*')
    .eq('listing_id', listingId)
    .eq('user_id', userId)
    .single();
  return data;
};

// ─── NOTIFICATIONS ────────────────────────────────────────
export const createNotification = async (userId, fromUserId, listingId, message) => {
  const { error } = await supabase
    .from('notifications')
    .insert([{ user_id: userId, from_user: fromUserId, listing_id: listingId, message }]);
  if (error) console.error('Notification error:', error.message);
};

export const getNotifications = async (userId) => {
  const { data, error } = await supabase
    .from('notifications')
    .select('*, listings(title)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(30);
  if (error) throw error;
  return data;
};

export const getUnreadCount = async (userId) => {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);
  if (error) return 0;
  return count || 0;
};

export const markAllRead = async (userId) => {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false);
  if (error) throw error;
};

export const markOneRead = async (id) => {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', id);
  if (error) throw error;
};
